import { useEffect, useState } from 'react'

const GOOGLE_TAG_MANAGER_SCRIPT = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window, document, 'script', 'dataLayer', 'GTM-WFDFDWP');`

export function GoogleTagManager() {
  const { geolocation, url: fidesUrl } = getFidesConfig()
  const shouldLoadGoogleTagManager = geolocation !== 'CA'

  return (
    <FidesGoogleTagManager
      shouldLoadGoogleTagManager={shouldLoadGoogleTagManager}
      scriptSrc={fidesUrl}
    />
  )
}

/**
 * Loads Fides/Ethyca script for consent and loads GTM after completion since it's a upstream dependency.
 */
export function FidesGoogleTagManager({
  shouldLoadGoogleTagManager,
  scriptSrc,
}: {
  shouldLoadGoogleTagManager: boolean
  scriptSrc: string
}) {
  const [fidesInitialized, setFidesInitialized] = useState(false)

  // Listen for Fides initialization and then load GTM
  useEffect(() => {
    function handleFidesInitialized() {
      setFidesInitialized(true)

      // @ts-ignore
      window.Fides.gtm()
    }

    window.addEventListener('FidesInitialized', handleFidesInitialized)

    return () => {
      window.removeEventListener('FidesInitialized', handleFidesInitialized)
    }
  }, [])

  // Need to insert the script at the beginning of the head tag before GTM
  useEffect(() => {
    const script = document.createElement('script')
    script.src = scriptSrc
    script.async = true

    const { head } = document
    if (head.firstChild) {
      head.insertBefore(script, head.firstChild)
    } else {
      head.appendChild(script)
    }

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script)
      }
    }
  }, [scriptSrc])

  // Load GTM after Fides is initialized
  useEffect(() => {
    if (!fidesInitialized || !shouldLoadGoogleTagManager) {
      return
    }

    // Load GTM script
    const gtmScript = document.createElement('script')
    gtmScript.innerHTML = GOOGLE_TAG_MANAGER_SCRIPT
    document.head.appendChild(gtmScript)

    // Load GTM noscript iframe
    const gtmNoScript = document.createElement('noscript')
    const gtmIframe = document.createElement('iframe')
    gtmIframe.src = 'https://www.googletagmanager.com/ns.html?id=GTM-WFDFDWP'
    gtmIframe.height = '0'
    gtmIframe.width = '0'
    gtmIframe.style.display = 'none'
    gtmIframe.style.visibility = 'hidden'
    gtmNoScript.appendChild(gtmIframe)
    document.body.insertBefore(gtmNoScript, document.body.firstChild)

    return () => {
      // Cleanup if needed
      if (gtmScript.parentNode) {
        gtmScript.parentNode.removeChild(gtmScript)
      }

      if (gtmNoScript.parentNode) {
        gtmNoScript.parentNode.removeChild(gtmNoScript)
      }
    }
  }, [fidesInitialized, shouldLoadGoogleTagManager])

  return null
}

function getFidesConfig() {
  const searchParams = new URLSearchParams(window.location.search)
  const fidesPrefix = 'fides_'

  const fidesSearchParams = new URLSearchParams()

  searchParams.forEach((value, key) => {
    if (key.startsWith(fidesPrefix)) {
      fidesSearchParams.set(
        key.replace(fidesPrefix, ''),
        key === `${fidesPrefix}cache_bust` ? Date.now().toString() : value
      )
    }
  })

  const geolocation = searchParams.get('fides_geolocation')
  return {
    geolocation,
    url: `https://privacycenter.ramp.com/fides.js?${fidesSearchParams.toString()}`,
  }
}
